import dom from "../../../dist/jsx-render/dom"
import JSXComponent from "../../../dist/jsx-render/JSXComponent"
import { ColumnBase } from "./ColumnBase";
import { Settings } from "./Settings";
import { MsoTable } from "./MsoTable";
import { FilterPopupComponent } from "./FilterPopupComponent";
import { FlexTableComponent } from "./FlexTableComponent";
import { SizingInfo } from "./SizingInfo";
import { app } from "../../Frame/App";

/**
 * Properties for FlexHeaderCellComponent
 * */
export type FlexHeaderCellComponentProps = {
    column: ColumnBase;
    settings: Settings;
    enableSort: boolean;
    table: MsoTable;
    enableScroll: boolean;
    enableFilterPopup: boolean;
    fixedLayout: boolean;
    flexTable: FlexTableComponent;
    flexCompleteWidth: boolean;
}

/**
 * Component to create a single cell. 
 * */
export class FlexHeaderCellComponent extends JSXComponent {
    props: FlexHeaderCellComponentProps;
    private sizing: SizingInfo;

    /**
     * Constructor
     * @param props Properties of this component.
     */
    constructor(props: FlexHeaderCellComponentProps) {
        super(props);
    }

    /**
     * Calculate css class.
     * @returns css class.
     * */
    getClassName(): string {
        let result = "mt-cell";
        if ((this.props.enableSort && this.props.column.isSortable) || (this.props.enableFilterPopup && this.props.column.isFilterable)) {
            result += " pointer";

            if ((this.props.enableSort && this.props.column.isSortable) && (this.props.column.field == this.props.settings.sortField)) {
                if (this.props.column.field == this.props.settings.sortField)
                    result += this.props.settings.sortAsc ? " sort-asc" : " sort-desc";
                //if (this.props.column.field == this.props.settings.sortSecondField)
                //    result += this.props.settings.sortSecondAsc ? " sort-asc" : " sort-desc";
            }
        }
        if (this.props.column.headerCssClass) {
            result += " " + this.props.column.headerCssClass;
        }
        return result;
    }

    /**
     * The HTML element of this component.
     * */
    element: HTMLElement =
        <div className={this.getClassName()}>
            <div className="mt-cell-content">
                <span className="mf-title">
                    {(this.props.column.headerText == "Dok") ? <i class='fas fa-paperclip'></i> : this.props.column.headerText ?? '\u00A0'}
                    {this.props.column.headerElement ?? []}

                </span>
            </div>
            {this.createColumnSizer()}
        </div>

    /**
     * The column sizer element.
     * */
    sizerElement: HTMLElement;

    /**
     * Create the column sizer element. Only created in flex layout.
     * */
    private createColumnSizer(): HTMLElement {

        if (!this.props.fixedLayout && this.props.column.isSizeable && !this.props.flexCompleteWidth) {
            this.sizerElement = <div className={this.props.column.sizerClass} data-column={this.props.column.number} data-field={this.props.column.field}></div>
            this.sizing = new SizingInfo();
            return this.sizerElement;
        }
        return null;
    }

    /**
     * Render the HTML element.
     * */
    render() {
        // add click event handler
        this.element.addEventListener("click", this.onCellClick);

        // subscribe to remove column sort event
        this.props.table.onRemoveColumnSort.subscribe(() => {
            this.element.classList.remove("sort-asc");
            this.element.classList.remove("sort-desc");
        });

        // subscribe to set column sort event
        this.props.table.onSetColumnSort.subscribe((field: string, asc: boolean) => {
            if (this.props.column.field == field) {
                this.element.classList.add(asc ? "sort-asc" : "sort-desc");
            }
        });

        // subscribe to column filter changed event
        this.props.table.onChangeColumnFilter.subscribe((field: string, on: boolean) => {
            if (this.props.column.field == field) {
                if (on) {
                    this.element.classList.add("filter-on");
                }
                else {
                    this.element.classList.remove("filter-on");
                }
            }
        });

        // add event handler for column sizing
        if (this.sizerElement) {
            this.sizerElement.addEventListener("mousedown", this.onSizerMouseDown);
        }

        if (this.props.fixedLayout) {
            if (this.props.column.fixedWidth) {
                let fixedWithNumber = Number(this.props.column.fixedWidth.replace("px", ""));

                if (!isNaN(fixedWithNumber)) {
                    this.element.style.width = Math.round(fixedWithNumber / window.devicePixelRatio) + "px";
                } else {
                    this.element.style.width = this.props.column.fixedWidth;
                }
            }
        } else {
            if (this.props.column.defaultWidth) {
                let defaultWidthAsNumber = Number(this.props.column.defaultWidth);
                if (!isNaN(defaultWidthAsNumber)) {
                    this.element.style.width = Math.round(defaultWidthAsNumber / window.devicePixelRatio) + "px";
                } else {
                    if (this.props.column.defaultWidth.indexOf("%") != -1) {
                        this.element.style.width = this.props.column.calculatePercentWith(this.props.column.defaultWidth);
                    } else {
                        this.element.style.width = this.props.column.defaultWidth;
                    }
                }
            }

            if (this.props.column.width) {
                this.element.style.width = Math.round(this.props.column.width / window.devicePixelRatio) + "px";
            }

        }

        return this.element;
    }

    /**
     * Mouse down event on column sizer.
     * @param ev Event data.
     */
    onSizerMouseDown = (ev: MouseEvent) => {
        this.sizing.pageX = ev.pageX;
        this.sizing.originalWidth = this.element.offsetWidth;
        this.sizing.newWidth = this.sizing.originalWidth;
        this.sizing.isSizing = true;

        ev.cancelBubble = true;
        ev.stopPropagation();
        ev.preventDefault();

        // listen on mousemove and mouseup
        document.addEventListener("mousemove", this.onDocumentMouseMove);
        document.addEventListener("mouseup", this.onDocumentMouseUp);
    }

    /**
     * Mouse move event handler.
     * @param ev Event data.
     */
    onDocumentMouseMove = (ev: MouseEvent) => {
        if (this.sizing.isSizing && !this.sizing.sizingInProgress) {
            this.sizing.sizingInProgress = true;
            const diffX = ev.pageX - this.sizing.pageX;
            const newWidth = this.sizing.originalWidth + diffX;
            if (newWidth >= 40) {
                this.sizing.newWidth = newWidth;
                const width = newWidth.toString() + "px";

                // TODO use this to reset the sizes - recalculate before unsing the same logic as when the table is created
                this.props.flexTable.setColumnWidth(this.props.column.index, width);

                if (!this.props.table.getRootElement().classList.contains("use-ellipsis"))
                    this.props.flexTable.lineBreakColumn(this.props.column.index, newWidth);
            }
            this.sizing.sizingInProgress = false;
            ev.cancelBubble = true;
            ev.preventDefault();
        }
    }

    /**
     * Mouse up event handler.
     * @param ev Event data.
     */
    onDocumentMouseUp = (ev: MouseEvent) => {
        if (this.sizing.isSizing) {
            ev.stopPropagation();
            ev.preventDefault();
            ev.cancelBubble = true;

            document.removeEventListener("mousemove", this.onDocumentMouseMove);
            document.removeEventListener("mouseup", this.onDocumentMouseUp);

            setTimeout(() => {
                this.sizing.isSizing = false;
            }, 10);

            // store new width in settings
            this.props.table.storeNewWidth(this.props.column.field, this.props.column.index, this.sizing.newWidth);

            // update column width
            this.props.column.width = this.sizing.newWidth;
            this.props.flexTable.setColumnWidth(this.props.column.index, this.sizing.newWidth + "px");
        }
    }

    /**
     * Event handler for cell click.
     * @param ev Event data.
     */
    onCellClick = (ev: MouseEvent) => {
        ev.stopPropagation();
        ev.preventDefault();

        let target = (ev.target as HTMLElement);
        if (target.classList.contains("mt-cell-content") || target.parentElement?.classList.contains("mt-cell-content")) {
            if (!this.sizing || !this.sizing.isSizing) {
                if (this.props.enableFilterPopup && this.props.column.isFilterable) {
                    // check if the filter popup is already show up, or not
                    let popupfilters = document.querySelectorAll(".mt-filter-popup");
                    if (popupfilters && popupfilters.length > 0) {
                        popupfilters.forEach((p) => {
                            p.remove();
                        })
                    } 

                    const filterPopup = new FilterPopupComponent({
                        column: this.props.column,
                        settings: this.props.settings,
                        enableSort: this.props.enableSort,
                        table: this.props.table,
                        cell: this.element
                    });
                    filterPopup.create();
                }
            }
            else if (this.props.enableSort) {
                // TODO
            }
        }
   
    }
}

